import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { fileUrl } from '../lib/api';
import { Bot, Send, Flame, PauseCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ErrorBoundary from '../components/shared/ErrorBoundary';

const ACTIVE_KEY = 'fabtexs.ai_assistant.active_id';

export default function AIAssistant() {
  return (
    <ErrorBoundary>
      <AIAssistantInner />
    </ErrorBoundary>
  );
}

function AIAssistantInner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [testPhone, setTestPhone] = useState('+919876500001');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const loadConvs = useCallback(() => {
    return api.get('/ai/conversations')
      .then(({ data }) => setConvs(Array.isArray(data?.items) ? data.items : []))
      .catch(() => setConvs([]));
  }, []);

  const openConv = useCallback(async (c) => {
    if (!c?.id) return;
    setActive(c);
    setSearchParams((sp) => { const n = new URLSearchParams(sp); n.set('conv', c.id); return n; });
    try { localStorage.setItem(ACTIVE_KEY, c.id); } catch (_) { /* ignore */ }
    if (c.customer_phone) setTestPhone(c.customer_phone);
    try {
      const { data } = await api.get(`/ai/conversations/${c.id}`);
      setMessages(Array.isArray(data?.messages) ? data.messages : []);
    } catch (e) {
      toast.error('Could not load conversation history');
      setMessages([]);
    }
  }, [setSearchParams]);

  // Initial load: pull conversations, then auto-open whichever the user had open
  // (URL ?conv=… wins over localStorage so deep-links work).
  useEffect(() => {
    (async () => {
      await loadConvs();
      let resumeId = searchParams.get('conv');
      if (!resumeId) {
        try { resumeId = localStorage.getItem(ACTIVE_KEY); } catch (_) { resumeId = null; }
      }
      if (resumeId) {
        try {
          const { data: c } = await api.get(`/ai/conversations/${resumeId}`);
          if (c?.id) {
            setActive(c);
            setMessages(Array.isArray(c?.messages) ? c.messages : []);
            if (c.customer_phone) setTestPhone(c.customer_phone);
          } else {
            // stale id — clear it
            try { localStorage.removeItem(ACTIVE_KEY); } catch (_) { /* ignore */ }
          }
        } catch (_) {
          try { localStorage.removeItem(ACTIVE_KEY); } catch (_) { /* ignore */ }
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try { endRef.current?.scrollIntoView({ behavior: 'smooth' }); } catch (_) { /* ignore */ }
  }, [messages]);

  // Live update — lightweight polling of the canonical conversation source
  // (reuses loadConvs; no second realtime system). Refreshes the list every 4s
  // and the open thread so the seller watches activity without refreshing.
  useEffect(() => {
    const iv = setInterval(() => {
      if (document.visibilityState !== 'visible' || loading) return;
      loadConvs();
      if (active?.id) {
        api.get(`/ai/conversations/${active.id}`)
          .then(({ data }) => { if (Array.isArray(data?.messages)) setMessages(data.messages); })
          .catch(() => {});
      }
    }, 4000);
    return () => clearInterval(iv);
  }, [loadConvs, active?.id, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    setLoading(true);
    const userMsg = input;
    setMessages((m) => [...m, { direction: 'INBOUND', content: userMsg, timestamp: new Date().toISOString() }]);
    setInput('');
    try {
      const { data } = await api.post('/ai/chat', {
        conversation_id: active?.id,
        customer_phone: testPhone,
        message: userMsg,
      });
      const reply = data?.reply ?? '';
      setMessages((m) => [...m, {
        direction: 'OUTBOUND',
        content: reply,
        timestamp: new Date().toISOString(),
        is_ai_generated: !data?.ai_paused,
        is_system: data?.ai_paused === true,
        products: Array.isArray(data?.products) ? data.products : [],
      }]);
      if (data?.escalated) toast.warning('🔥 Escalated to seller — AI paused for this conversation');
      else if (data?.is_hot_lead) toast.success(`🔥 Hot Lead! Score: ${data.lead_score}/10`);
      const convId = data?.conversation_id || active?.id;
      if (convId) {
        try {
          const { data: c } = await api.get(`/ai/conversations/${convId}`);
          if (c?.id) {
            setActive(c);
            try { localStorage.setItem(ACTIVE_KEY, c.id); } catch (_) { /* ignore */ }
            setSearchParams((sp) => { const n = new URLSearchParams(sp); n.set('conv', c.id); return n; });
          }
        } catch (_) { /* non-fatal */ }
      }
      loadConvs();
    } catch (err) {
      toast.error('AI failed to respond');
    } finally {
      setLoading(false);
    }
  };

  const handBackToAI = async () => {
    if (!active?.id) return;
    try {
      await api.post(`/ai/conversations/${active.id}/handback`);
      const { data: c } = await api.get(`/ai/conversations/${active.id}`);
      if (c?.id) setActive(c);
      loadConvs();
      toast.success('AI resumed — it will reply to the next buyer message');
    } catch (e) {
      toast.error('Could not hand back');
    }
  };

  return (
    <div className="space-y-5 fade-up" data-testid="ai-assistant-page">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="fab-card p-4 bg-gradient-to-br from-fab-primary-lighter to-white">
          <div className="flex items-center gap-2"><Bot className="w-5 h-5 text-fab-primary" /><div className="font-display font-bold text-fab-text">AI Active</div></div>
          <div className="text-[11px] text-fab-wa-dark mt-1 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-fab-wa live-dot" /> Listening</div>
        </div>
        <div className="fab-card p-4"><div className="text-[11px] text-fab-text-muted uppercase tracking-wider">Conversations</div><div className="font-display text-2xl font-bold text-fab-text">{convs.length}</div></div>
        <div className="fab-card p-4"><div className="text-[11px] text-fab-text-muted uppercase tracking-wider">🔥 Hot Leads</div><div className="font-display text-2xl font-bold text-fab-orange">{convs.filter(c => c.is_hot_lead).length}</div></div>
        <div className="fab-card p-4"><div className="text-[11px] text-fab-text-muted uppercase tracking-wider">Active Now</div><div className="font-display text-2xl font-bold text-fab-text">{convs.filter(c => c.status === 'ACTIVE').length}</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="fab-card p-0 lg:col-span-1 overflow-hidden flex flex-col" style={{ maxHeight: '70vh' }}>
          <div className="p-3 border-b border-fab-primary-lighter font-display font-bold text-fab-text flex items-center justify-between">
            Live Conversations
            <button onClick={loadConvs} className="text-fab-text-muted hover:text-fab-primary" title="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {convs.map((c) => (
              <button
                key={c.id}
                onClick={() => openConv(c)}
                data-testid={`conv-${c.id}`}
                className={`w-full text-left p-3 border-b border-fab-primary-lighter hover:bg-fab-elevated ${active?.id === c.id ? 'bg-fab-primary-lighter' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[13px] font-semibold text-fab-text truncate">{c.customer_name || c.customer_phone || 'Buyer'}</div>
                  {c.is_hot_lead && <Flame className="w-4 h-4 text-fab-orange shrink-0" />}
                </div>
                {c.customer_name && c.customer_phone && (
                  <div className="text-[11px] text-fab-text-muted truncate">{c.customer_phone}</div>
                )}
                {c.last_message && (
                  <div className="text-[12px] text-fab-text/80 truncate mt-0.5" data-testid={`conv-preview-${c.id}`}>
                    {c.last_message_direction === 'OUTBOUND' ? '↩ ' : ''}{c.last_message}
                  </div>
                )}
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${c.needs_human ? 'bg-fab-orange/15 text-fab-orange' : 'bg-fab-primary-lighter text-fab-primary'}`} data-testid={`conv-handling-${c.id}`}>
                    {c.needs_human ? 'SELLER ADVICE REQUIRED' : 'AI HANDLING'}
                  </span>
                  <span className="text-[11px] text-fab-text-muted">
                    Score: {c.lead_score ?? 0}/10{c.last_message_at && ` · ${new Date(c.last_message_at).toLocaleTimeString()}`}
                  </span>
                </div>
              </button>
            ))}
            {convs.length === 0 && <div className="p-4 text-[12px] text-fab-text-muted">No conversations yet. Try the test chat →</div>}
          </div>
        </div>

        <div className="fab-card p-0 lg:col-span-2 overflow-hidden flex flex-col" style={{ height: '70vh' }}>
          <div className="p-3 border-b border-fab-primary-lighter bg-fab-wa-light/30">
            <div className="font-display font-bold text-fab-text text-[13px]">Test Chat with AI (Claude Sonnet 4.5)</div>
            <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Customer phone" className="mt-1 text-[11px] font-mono text-fab-text-muted bg-transparent outline-none w-full" />
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#ECE5DD]/40">
            {messages.length === 0 && <div className="text-center text-fab-text-muted text-[12px] mt-10">Say hi to start! Try: &ldquo;Do you have KUR-1005?&rdquo;</div>}
            {messages.map((m, i) => (
              <MessageBubble key={i} m={m} i={i} />
            ))}
            <div ref={endRef} />
          </div>
          {active?.ai_enabled === false && (
            <div className="px-3 py-2 bg-amber-50 border-t border-amber-200 flex items-center justify-between gap-2">
              <div className="text-[12px] text-amber-900 flex items-center gap-1.5"><PauseCircle className="w-4 h-4" /> AI paused — buyer escalated. Take over manually or hand back.</div>
              <button onClick={handBackToAI} data-testid="handback-btn" className="px-3 py-1 rounded bg-fab-wa text-white text-[11px] font-semibold">Hand back to AI</button>
            </div>
          )}
          <div className="p-3 border-t border-fab-primary-lighter flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
              placeholder="Type a buyer message..."
              className="flex-1 fab-input px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
              data-testid="ai-input"
              disabled={loading}
            />
            <button onClick={send} disabled={loading || !input.trim()} data-testid="ai-send" className="btn-wa px-4 py-2 rounded-lg font-semibold flex items-center gap-1 disabled:opacity-50"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** One message bubble — wrapped in its own ErrorBoundary so a single malformed
 *  message (bad product url, missing timestamp, weird structure) can't blank
 *  the entire chat thread. */
function MessageBubble({ m, i }) {
  return (
    <ErrorBoundary>
      <SafeMessage m={m} i={i} />
    </ErrorBoundary>
  );
}

function SafeMessage({ m, i }) {
  if (!m || typeof m !== 'object') return null;
  const direction = m.direction === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND';
  const content = typeof m.content === 'string' ? m.content : (m.content == null ? '' : String(m.content));
  const tsRaw = m.timestamp || m.created_at;
  let timeStr = '';
  try {
    if (tsRaw) {
      const d = new Date(tsRaw);
      if (!isNaN(d.getTime())) timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  } catch (_) { /* ignore */ }
  const products = Array.isArray(m.products) ? m.products : [];

  return (
    <div className={`flex ${direction === 'OUTBOUND' ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] flex flex-col gap-2 ${direction === 'OUTBOUND' ? 'items-end' : 'items-start'}`}>
        <div
          className={`px-3 py-2 rounded-2xl text-[13px] shadow-sm whitespace-pre-wrap ${
            m.is_system ? 'bg-amber-100 text-amber-900 border border-amber-300' :
            direction === 'OUTBOUND' ? 'bg-fab-wa-light text-fab-text' : 'bg-white text-fab-text'
          }`}
          data-testid={`msg-${i}`}
        >
          {m.is_system && (
            <span className="inline-flex items-center gap-1 mr-1 text-amber-700 font-semibold text-[11px]">
              <PauseCircle className="w-3 h-3" /> Seller takeover
            </span>
          )}
          {content || <em className="text-fab-text-muted">(empty message)</em>}
          {timeStr && <div className="text-[9px] text-fab-text-muted mt-1 text-right">{timeStr}</div>}
        </div>
        {products.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 w-full max-w-md" data-testid={`product-cards-${i}`}>
            {products.map((p, idx) => {
              if (!p || typeof p !== 'object') return null;
              const code = p.product_code || p.code || `P${idx}`;
              const imageSrc = (() => {
                try {
                  if (p.image_url) return fileUrl(p.image_url);
                  if (p.processed_image_url) return fileUrl(p.processed_image_url);
                } catch (_) { /* ignore */ }
                return null;
              })();
              return (
                <div key={`${code}-${idx}`} className="bg-white rounded-xl overflow-hidden shadow-sm border border-fab-primary-lighter" data-testid={`card-${code}`}>
                  {imageSrc && (
                    <div className="aspect-square bg-fab-elevated">
                      <img src={imageSrc} alt={code} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                  )}
                  <div className="px-2 py-1.5 space-y-0.5">
                    <div className="font-mono font-bold text-[11px] text-fab-text">{code}</div>
                    <div className="text-[11px] text-fab-text font-semibold">₹{Number(p.price || 0).toLocaleString('en-IN')}</div>
                    <div className="text-[10px] text-fab-text-muted">MOQ {p.moq ?? '—'} · Stock {p.stock_available ?? p.stock ?? '—'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
